
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Shield, Clock, Calendar } from 'lucide-react';
import { MonthYearSelect } from '@/components/teacher/MonthYearSelect';
import { TeacherMonthlySummary } from '@/components/teacher/TeacherMonthlySummary';
import { TeacherReportDetails } from '@/components/teacher/TeacherReportDetails';
import { SalaryVerificationSteps } from '@/components/teacher/salary/SalaryVerificationSteps';
import { AvailabilityCalendar } from '@/components/teacher/availability/AvailabilityCalendar';
import { WeeklyScheduleManager } from '@/components/teacher/availability/WeeklyScheduleManager';
import { ExceptionsManager } from '@/components/teacher/availability/ExceptionsManager';
import { useTeacherSalary } from '@/hooks/useTeacherSalary';
import { useSalaryVerification } from '@/hooks/useSalaryVerification';
import { useProfile } from '@/hooks/useProfile';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const TeacherSalaryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : 'th-TH';
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = React.useState(2026);
  const { profile } = useProfile();

  const { monthlyReport, isLoading, reportDetails, refetch, teacherCode, isTaxPnd3 } = useTeacherSalary(selectedMonth, selectedYear);
  const { 
    settings, 
    verification, 
    isVerificationRequired, 
    canAccessSalary, 
    verifyTeacher,
    isLoading: isVerificationLoading 
  } = useSalaryVerification(selectedMonth, selectedYear);

  const handleVerification = async (data: any) => {
    const success = await verifyTeacher(data);
    return success;
  };

  if (isLoading || isVerificationLoading) {
    return (
      <div className="min-h-screen bg-gray-50 w-full">
        <header className="bg-form-blue text-white py-6 shadow-md relative w-full">
          <div className="w-full px-4 flex items-center">
            <Link 
              to="/" 
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 hover:bg-blue-600 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div className="flex-grow text-center">
              <h1 className="text-2xl md:text-3xl font-bold">{t('salary.title')}</h1>
              <p className="mt-2 text-blue-100">{t('salary.subtitle')}</p>
            </div>
          </div>
        </header>

        <main className="w-full px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 w-full">
            <div>{t('salary.loading')}</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <header className="bg-form-blue text-white py-6 shadow-md relative w-full">
        <div className="w-full px-4 flex items-center">
          <Link 
            to="/" 
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 hover:bg-blue-600 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div className="flex-grow text-center">
            <h1 className="text-2xl md:text-3xl font-bold">{t('salary.title')}</h1>
            <p className="mt-2 text-blue-100">{t('salary.subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="w-full px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 w-full">
          <Tabs defaultValue="salary" className="w-full">
            <TabsList className="grid w-full max-w-full grid-cols-2 mb-6 h-auto">
              <TabsTrigger value="salary" className="text-xs sm:text-sm px-2 py-2 min-h-0">
                {t('salary.tabSalary')}
              </TabsTrigger>
              <TabsTrigger value="availability" className="text-xs sm:text-sm px-2 py-2 min-h-0">
                <span className="hidden sm:inline">{t('salary.tabAvailabilityFull')}</span>
                <span className="sm:hidden">{t('salary.tabAvailabilityShort')}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="salary">
              <MonthYearSelect
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                onMonthChange={setSelectedMonth}
                onYearChange={setSelectedYear}
              />

              {isVerificationRequired && profile?.teacher_id && (
                <div className="mb-6">
                  <SalaryVerificationSteps
                    settings={settings}
                    teacherCode={profile.teacher_id}
                    onVerify={handleVerification}
                  />
                </div>
              )}

              {canAccessSalary ? (
                <>
                  {verification?.is_verified && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700">
                        <Shield className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {t('salary.verifiedAt', { date: new Date(verification.verified_at!).toLocaleString(dateLocale) })}
                        </span>
                      </div>
                    </div>
                  )}

                  {monthlyReport ? (
                    <>
                      <TeacherMonthlySummary 
                        monthlyReport={monthlyReport} 
                        teacherCode={teacherCode}
                        isTaxPnd3={isTaxPnd3}
                        onConfirm={refetch}
                        onRefetch={refetch}
                      />
                      <div className="mt-6 w-full">
                        <TeacherReportDetails reportDetails={reportDetails} totalSalaryFromReport={monthlyReport.total_salary} />
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      {t('salary.noData')}
                    </div>
                  )}
                </>
              ) : null}
            </TabsContent>

            <TabsContent value="availability">
              {profile?.teacher_id ? (
                <div className="space-y-6">
                  {/* ภาพรวม */}
                  <div>
                    <AvailabilityCalendar teacherCode={profile.teacher_id} />
                  </div>

                  {/* แยก tabs สำหรับการจัดการ */}
                  <div>
                    <Tabs defaultValue="weekly" className="w-full">
                      <TabsList className="grid w-full max-w-full grid-cols-2 h-auto">
                        <TabsTrigger value="weekly" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 min-h-0">
                          <Clock size={14} className="sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">{t('salary.tabWeeklyFull')}</span>
                          <span className="sm:hidden">{t('salary.tabWeeklyShort')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="exceptions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 min-h-0">
                          <Calendar size={14} className="sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">{t('salary.tabExceptionsFull')}</span>
                          <span className="sm:hidden">{t('salary.tabExceptionsShort')}</span>
                        </TabsTrigger>
                      </TabsList>
                      
                      <div className="mt-6">
                        <TabsContent value="weekly">
                          <WeeklyScheduleManager teacherCode={profile.teacher_id} />
                        </TabsContent>
                        <TabsContent value="exceptions">
                          <ExceptionsManager teacherCode={profile.teacher_id} />
                        </TabsContent>
                      </div>
                    </Tabs>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t('salary.noTeacherCodeAvailability')}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default TeacherSalaryPage;
